<script setup lang="ts">
	import { cn } from '@/lib/utils'
	import { Info } from 'lucide-vue-next'
	import { computed, useId } from 'vue'

	import { Label } from '@/components/ui/label'
	import {
		Popover,
		PopoverContent,
		PopoverTrigger,
	} from '@/components/ui/popover'

	/**
	 * Etiqueta, control, y la aclaración detrás de un botón ⓘ.
	 *
	 * La aclaración empezó como un párrafo gris debajo del campo. Con varios
	 * campos seguidos, el formulario se volvía un muro de texto y la vista se
	 * acostumbraba a saltárselo — justo donde después sí hace falta leerlo. Al
	 * botón se entra solo si se quiere, y el espacio de abajo queda libre.
	 *
	 * Es popover al clic, no tooltip al hover: un tooltip no abre en una
	 * pantalla táctil, y ahí el ⓘ sería un botón que no hace nada.
	 *
	 * El error NO se esconde: si el campo está mal, la persona tiene que verlo
	 * sin tener que descubrir que hay algo que abrir.
	 *
	 * El `id` se genera acá y se pasa al slot para que el control lo use: así
	 * el label y la aclaración quedan asociados al input de verdad
	 * (`for` / `aria-describedby`), no solo puestos cerca.
	 */
	interface Props {
		label?: string
		/** Marca visual de obligatorio. */
		required?: boolean
		/** Qué es el campo y qué consecuencia tiene. Va detrás del botón ⓘ. */
		hint?: string
		/** Reemplaza al hint mientras esté presente, y sí se muestra siempre. */
		error?: string
		class?: string
	}

	const props = defineProps<Props>()

	const uid = useId()
	const controlId = computed(() => `field-${uid}`)
	const labelId = computed(() => `field-${uid}-label`)
	const hintId = computed(() => `field-${uid}-hint`)

	const describedBy = computed(() =>
		props.error || props.hint ? hintId.value : undefined
	)
</script>

<template>
	<div :class="cn('flex flex-col gap-1.5', props.class)">
		<div
			v-if="label"
			class="flex items-center gap-1"
		>
			<Label
				:id="labelId"
				:for="controlId"
				class="text-sm font-medium text-gray-900"
			>
				{{ label }}
				<span
					v-if="required"
					class="text-red-500"
					aria-hidden="true"
					>*</span
				>
			</Label>

			<Popover v-if="hint">
				<PopoverTrigger as-child>
					<button
						type="button"
						class="rounded-full text-gray-400 transition-colors hover:text-[#253C70] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						:aria-label="`Qué es ${label}`"
					>
						<Info class="h-3.5 w-3.5" />
					</button>
				</PopoverTrigger>

				<PopoverContent
					:id="hintId"
					align="start"
					class="w-72 text-xs leading-relaxed text-gray-600"
				>
					{{ hint }}
				</PopoverContent>
			</Popover>
		</div>

		<!--
			`labelId` es para los controles que no son un input —un grupo de campos,
			como el horario—: ahí `for` no asocia nada y hace falta `aria-labelledby`.
		-->
		<slot
			:id="controlId"
			:label-id="labelId"
			:described-by="describedBy"
			:invalid="!!error"
		/>

		<p
			v-if="error"
			:id="hintId"
			class="text-xs leading-snug text-red-600"
		>
			{{ error }}
		</p>
	</div>
</template>
